import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import BackgroundImage from "../../../components/ui/BackgroundImage";
import dangerBg from "../assets/images/danger-bg.png";
import Button from "./ui/Button";
import { MENU_SCREEN } from "../menu.types";
import discordImg from "../assets/images/discord.png";
import APCoins from "./ui/APCoins";
import { IoReturnDownBack } from "react-icons/io5";
import { useAudioEffects } from "../../../hooks/use-audio-effects";
import clickSound from "../assets/audio/click.wav";
import navigationSound from "../assets/audio/menu-open.wav";
import DiscordLinkingModal from "./DiscordLinkingModal/DiscordLinkingModal";
import {
  headerVariants,
  accentBarVariants,
  wordVariants,
  rightSectionVariants,
  exitVariants,
} from "./animations/menuHeader.variants";

export type MenuTitleType = { text: string; isHighlighted?: boolean }[];

interface Props {
  className?: string;
  titleWords: MenuTitleType;
  titleClassName?: string;
  apoints: number;
  description?: string;
  pointsCtaTitle?: string;
  rewardsCtaTitle?: string;
  previousScreen?: MENU_SCREEN;
  backCtaTitle?: string;
  onBack?: () => void;
}

const MenuHeader: React.FC<Props> = ({
  className,
  titleWords = [],
  titleClassName = "",
  description,
  pointsCtaTitle = "CUMPARA ATLANTIS POINTS",
  rewardsCtaTitle = "Conectează Discord",
  previousScreen,
  apoints,
  backCtaTitle = "Back",
  onBack,
}) => {
  const rm = useReducedMotion();
  const { playEffect } = useAudioEffects();
  const [isDiscordModalOpen, setIsDiscordModalOpen] = useState(false);

  return (
    <>
    <motion.div
      className={`rounded-xl p-6 px-10 bg-gradient-to-r from-[#1785EF]/[0.04] via-[#1785EF]/[0.03] to-[#312f3c]/50 border border-white/[0.07] flex items-center justify-between ${className}`}
      variants={rm ? undefined : headerVariants}
      initial={rm ? undefined : "hidden"}
      animate={rm ? undefined : "visible"}
    >
      {/** Title & Description */}
      <div>
        <motion.div
          className="h-[4px] w-[40px] bg-[#1A8BF7] rounded-sm [box-shadow:_0_3px_3px_rgba(26,139,247,0.25)]"
          variants={rm ? undefined : accentBarVariants}
          initial={rm ? undefined : "hidden"}
          animate={rm ? undefined : "visible"}
        />
        <h1
          className={`uppercase text-[2.85rem] font-bold flex items-center flex-wrap text-wrap gap-2 leading-[1] mt-3 mb-2  ${titleClassName}`}
        >
          {titleWords?.map((word, i) => (
            <motion.span
              key={i}
              className={`${
                word.isHighlighted
                  ? "text-[#1A8BF7] [text-shadow:_0_6px_3px_rgba(26,139,247,0.25)]"
                  : "bg-clip-text text-transparent bg-gradient-to-r from-white to-white/50"
              }`}
              variants={rm ? undefined : wordVariants}
              initial={rm ? undefined : "hidden"}
              animate={rm ? undefined : "visible"}
              custom={i}
            >
              {word.text}
            </motion.span>
          ))}
        </h1>
        {description && (
          <p className="text-white/40 chakra-petch-font text-[0.85rem] leading-[0.6]">
            {description}
          </p>
        )}
      </div>

      <motion.div
        className="flex items-center space-x-10"
        variants={rm ? undefined : rightSectionVariants}
        initial={rm ? undefined : "hidden"}
        animate={rm ? undefined : "visible"}
      >
        {/** Sign in to rewards & Buy ap coins & Back*/}
        <div className="flex items-center space-x-3">
          {previousScreen && (
            <motion.button
              title="Back"
              onClick={() => {
                playEffect(navigationSound);
                if (onBack) {
                  onBack();
                }
              }}
              className="mr-10 chakra-petch-font uppercase flex items-center space-x-1 text-white/60 bg-[#3b546d] border-[1px] border-white/15 rounded-mx py-1 px-3 rounded-lg hover:bg-white hover:text-black transition"
              whileHover={rm ? undefined : { scale: 1.07, y: -2 }}
              whileTap={rm ? undefined : { scale: 0.91, y: 1 }}
              transition={{ type: "spring", stiffness: 420, damping: 22 }}
            >
              <IoReturnDownBack />
              <span className="text-[.9rem] font-semibold">{backCtaTitle}</span>
            </motion.button>
          )}

          <APCoins apoints={apoints} />

          <Button
            title={rewardsCtaTitle}
            variant="purple"
            className="text-white min-h-[52px] min-w-[190px] px-4 flex items-center !text-[18px]"
            onClick={() => {
              playEffect(clickSound);
              setIsDiscordModalOpen(true);
            }}
          >
            <img
              src={discordImg}
              alt="dis"
              width={17.5}
              height={13.5}
              className="mr-2"
            />
            {rewardsCtaTitle}
          </Button>
          <Button
            title={pointsCtaTitle}
            variant="green"
            className="text-white min-h-[52px] min-w-[190px] px-4  !text-[18px]"
            onClick={() => playEffect(clickSound)}
          >
            {pointsCtaTitle}
          </Button>
        </div>

        {/** Exit */}
        <motion.div
          className="flex items-center space-x-3 group"
          variants={rm ? undefined : exitVariants}
          initial={rm ? undefined : "hidden"}
          animate={rm ? undefined : "visible"}
        >
          <div className="text-right">
            <h2 className="uppercase text-white text-[1.55rem] font-semibold leading-[1] group-hover:text-[#FF4A4A] transition">
              Exit
            </h2>
            <p className="text-white/40 text-[0.9rem] gilroy-font">
              Close main menu
            </p>
          </div>

          <motion.button
            title="Exit"
            className="cursor-pointer transition hover:opacity-80 active:scale-90"
            whileHover={rm ? undefined : { scale: 1.12, rotate: 5 }}
            whileTap={rm ? undefined : { scale: 0.88, rotate: -5 }}
          >
            <BackgroundImage
              imageSrc={dangerBg}
              className="min-w-[54px] min-h-[54px] p-2"
              imageClassName="!object-fill"
            >
              <span className="text-[26px] font-semibold text-[#FF4A4A]">
                X
              </span>
            </BackgroundImage>
          </motion.button>
        </motion.div>
      </motion.div>
    </motion.div>

      <DiscordLinkingModal
        isOpen={isDiscordModalOpen}
        onClose={() => setIsDiscordModalOpen(false)}
        onConnect={() => setIsDiscordModalOpen(false)}
      />
    </>
  );
};

export default MenuHeader;
